/**
 * The one status dot (RFC 0330, follow-up).
 *
 * EIGHT COPIES BEFORE THIS, and their colours contradicted each other: yellow
 * meant "running" on a tool row and "waiting on you" on a space, blue meant
 * "working" in the composer strip and "waiting on you" above a question. A dot
 * is read at a glance or not at all, so one colour has to mean one thing
 * everywhere. The map below is the only place a status gets a colour.
 *
 * MOTION MEANS NOT FINISHED. Everything still in flight pulses; a settled
 * result (ok, error) or nothing at all (idle) is still.
 */

export type DotStatus =
  | 'working'
  | 'asking'
  | 'queued'
  | 'reading'
  | 'ok'
  | 'error'
  | 'idle'

const DOT: Record<DotStatus, { className: string; label: string }> = {
  working: { className: 'animate-pulse bg-blue-500', label: 'working' },
  asking: { className: 'animate-pulse bg-yellow-500', label: 'waiting on you' },
  queued: {
    className: 'animate-pulse bg-orange-400',
    label: 'waiting for a worker',
  },
  reading: {
    className: 'animate-pulse bg-muted-foreground/60',
    label: 'reading',
  },
  ok: { className: 'bg-green-500', label: 'done' },
  error: { className: 'bg-red-500', label: 'error' },
  idle: { className: 'bg-muted-foreground/40', label: 'nothing needs you' },
}

/**
 * A session's dot, from what the registry or the list knows (RFC 0311 3e).
 *
 * Filed as done wins, as it does in the list's grouping; then asking, working
 * and queued. Idle and unknown draw nothing: a quiet session needs no mark, and
 * one nobody has reported on must not be drawn as quiet.
 */
export function sessionDot(
  status: string | undefined,
  completedAt?: number,
): DotStatus | undefined {
  if (completedAt != null) return 'ok'
  if (status === 'asking') return 'asking'
  if (status === 'running') return 'working'
  if (status === 'queued') return 'queued'
  return undefined
}

export function StatusDot({
  status,
  title,
  className = '',
}: {
  status: DotStatus
  /** What a hover says; the status's own word when absent. */
  title?: string
  className?: string
}) {
  const dot = DOT[status]
  return (
    <span
      role="img"
      aria-label={title ?? dot.label}
      title={title ?? dot.label}
      className={`inline-block size-1.5 shrink-0 rounded-full ${dot.className} ${className}`}
    />
  )
}
